import { css } from '@emotion/react';
import cns from 'classnames';
import { FC, MouseEventHandler, ReactNode, useRef } from 'react';
import { useOnClickOutside } from '../../hooks';

export const DROPDOWN_CLASSNAME = '__mapui_dropdown';

export type XPlacement = 'left' | 'right';

export type YPlacement = 'top' | 'bottom';

export const X_PLACEMENT = {
  left: 'left',
  right: 'right',
};

export const Y_PLACEMENT = {
  top: 'top',
  bottom: 'bottom',
};

export interface DropDownProps {
  className?: string;
  content: ReactNode;
  onClick?: MouseEventHandler;
  onClickOutside?: (event: MouseEvent) => void;
  open?: boolean;
  placementX?: XPlacement;
  placementY?: YPlacement;
  trigger: ReactNode;
}

const applyMainStyles = () => (theme) => {
  return css(theme.dropdown.defaultStyles);
};

const triggerStyle = () => (theme) => {
  return css(theme.dropdown.trigger);
};

const contentStyle =
  (placementX: XPlacement, placementY: YPlacement, triggerHeight?: number) =>
  (theme) => {
    const content = css(theme.dropdown.content);

    const styles = [content];
    if (placementY === 'top') {
      styles.push(css(theme.dropdown.direction.top));
      if (triggerHeight !== undefined) {
        styles.push(css({ paddingBottom: triggerHeight }));
      }
    }
    if (placementY === 'bottom') {
      styles.push(css(theme.dropdown.direction.bottom));
    }

    styles.push(css(theme.dropdown.direction[placementX]));

    return css(...styles);
  };

export const DropDown: FC<DropDownProps> = ({
  className,
  content,
  onClick,
  onClickOutside,
  open = false,
  placementX = 'left',
  placementY = 'bottom',
  trigger,
}: DropDownProps) => {
  const dropDownRef = useRef<HTMLDivElement>(null);

  const handleTriggerClick: MouseEventHandler<HTMLDivElement> = (evt) => {
    if (typeof onClick === 'function') {
      onClick(evt);
    }
  };

  const handleOutsideClick: (event: MouseEvent) => void = (evt) => {
    if (typeof onClickOutside === 'function') {
      onClickOutside(evt);
    }
  };

  useOnClickOutside(dropDownRef, handleOutsideClick);

  return (
    <div
      className={cns(DROPDOWN_CLASSNAME, className)}
      ref={dropDownRef}
      css={applyMainStyles()}
    >
      <div
        onClick={handleTriggerClick}
        role="presentation"
        css={triggerStyle()}
      >
        {trigger}
      </div>
      {open && (
        <div
          css={contentStyle(
            placementX,
            placementY,
            dropDownRef?.current?.clientHeight
          )}
        >
          {content}
        </div>
      )}
    </div>
  );
};
